import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
  Tabs,
  Tab,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  Download,
  Description as DescriptionIcon,
  TableChart as TableChartIcon,
  UploadFile as UploadFileIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import ContactErnieModal from '../components/ContactErnieModal';
import NotifyCustomerModal from '../components/NotifyCustomerModal';
import ReportTypeChip from '../components/ReportTypeChip';
import { CoreData, OwnershipData, RPData, InspectionData, Report } from '../types';
import UnderstaffingSummary from '../components/facility/UnderstaffingSummary';
import FinancialsTab from '../components/facility/FinancialsTab';
import AcuityGapCharts from '../components/facility/AcuityGapCharts';
import StaffingTrendCard from '../components/facility/StaffingTrendCard';
import PriorConduct from '../components/facility/PriorConduct';
import OwnershipTab from '../components/facility/OwnershipTab';
import RelatedPartySection from '../components/facility/RelatedPartySection';
import ErnieInsights from '../components/facility/ErnieInsights';

/* ───────────── helpers ───────────── */

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index} style={{ display: value === index ? 'block' : 'none' }}>
      <Box sx={{ py: 3 }}>{children}</Box>
    </div>
  );
}

const tabMap: Record<string, number> = {
  staffing: 0,
  financials: 1,
  conduct: 2,
  insights: 3,
  ownership: 4,
  documents: 5,
};
const tabNames = ['staffing', 'financials', 'conduct', 'insights', 'ownership', 'documents'];


const formatAddress = (f: { street_address?: string | null; city?: string | null; state?: string | null; zip_code?: string | null } | null | undefined): string => {
  if (!f) return '';
  const digits = (f.zip_code || '').replace(/\D/g, '');
  const zip = digits.length === 9 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits.slice(0, 5);
  const cityState = [f.city, f.state].filter(Boolean).join(', ');
  const cityStateZip = [cityState, zip].filter(Boolean).join(' ');
  return [f.street_address, cityStateZip].filter(Boolean).join(', ');
};

/* ───────────── component ───────────── */

export default function ReportPage() {
  const { uuid } = useParams<{ uuid: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const tabParam = searchParams.get('tab');
  const [tabValue, setTabValue] = useState(tabParam && tabMap[tabParam] !== undefined ? tabMap[tabParam] : 0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    const newParams = new URLSearchParams(searchParams);
    if (newValue === 0) {
      newParams.delete('tab');
    } else {
      newParams.set('tab', tabNames[newValue]);
    }
    setSearchParams(newParams, { replace: true });
  };

  // Report (analysis) state
  const [analysis, setAnalysis] = useState<Report | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [notifyModalOpen, setNotifyModalOpen] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(true);

  // Facility data state
  const [coreData, setCoreData] = useState<CoreData | null>(null);
  const [ownershipData, setOwnershipData] = useState<OwnershipData | null>(null);
  const [rpData, setRpData] = useState<RPData | null>(null);
  const [inspectionData, setInspectionData] = useState<InspectionData | null>(null);
  const [coreLoading, setCoreLoading] = useState(false);
  const [facilityError, setFacilityError] = useState<string | null>(null);
  const [ownershipLoading, setOwnershipLoading] = useState(false);
  const [rpLoading, setRpLoading] = useState(false);
  const [inspectionLoading, setInspectionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acuityModel, setAcuityModel] = useState<'anticipated' | 'hybrid'>('anticipated');

  const ownershipFetchedRef = useRef(false);
  const rpFetchedRef = useRef(false);
  const inspectionFetchedRef = useRef(false);

  // 1. Fetch the analysis/report by UUID
  useEffect(() => {
    if (!uuid) return;
    setAnalysisLoading(true);
    setError(null);

    api.get(`/reports/${uuid}/`)
      .then(res => setAnalysis(res.data))
      .catch(err => {
        if (err.response?.status === 404) {
          setError('Report not found');
        } else {
          setError('Failed to load report');
        }
      })
      .finally(() => setAnalysisLoading(false));
  }, [uuid]);

  // 2. Once we have the analysis, fetch facility data by CCN (filtered to report years)
  useEffect(() => {
    if (!analysis) return;
    const ccn = analysis.ccn;
    const years = analysis.report_years;
    const params: Record<string, string> = {};
    if (years?.length) {
      params.years = years.join(',');
    }

    setCoreLoading(true);
    api.get(`/facilities/${ccn}/detail/`, { params, timeout: 30000 })
      .then(res => setCoreData(res.data))
      .catch(() => setFacilityError('Failed to load facility data'))
      .finally(() => setCoreLoading(false));
  }, [analysis]);

  // Fetch ownership
  const fetchOwnership = useCallback(async () => {
    if (!analysis || ownershipFetchedRef.current) return;
    ownershipFetchedRef.current = true;
    setOwnershipLoading(true);
    try {
      const state = coreData?.facility?.state || analysis.state;
      const res = await api.get(`/facilities/${analysis.ccn}/ownership/`, { params: { state } });
      setOwnershipData(res.data);
    } catch (err) {
      console.error('Failed to fetch ownership', err);
    }
    setOwnershipLoading(false);
  }, [analysis, coreData?.facility?.state]);

  // Fetch related parties
  const fetchRP = useCallback(async () => {
    if (!analysis || rpFetchedRef.current) return;
    rpFetchedRef.current = true;
    setRpLoading(true);
    try {
      const res = await api.get(`/facilities/${analysis.ccn}/related-parties/`);
      setRpData(res.data);
    } catch (err) {
      console.error('Failed to fetch related parties', err);
    }
    setRpLoading(false);
  }, [analysis]);

  // Fetch inspections
  const fetchInspections = useCallback(async () => {
    if (!analysis || inspectionFetchedRef.current) return;
    inspectionFetchedRef.current = true;
    setInspectionLoading(true);
    try {
      const res = await api.get(`/facilities/${analysis.ccn}/inspections/`);
      setInspectionData(res.data);
    } catch (err) {
      console.error('Failed to fetch inspections', err);
    }
    setInspectionLoading(false);
  }, [analysis]);

  // Background prefetch after core loads
  useEffect(() => {
    // Mediation reports show no facility tabs, so nothing to prefetch.
    if (!coreData || !analysis || analysis.report_type === 'mediation') return;
    const timer = setTimeout(() => {
      fetchOwnership();
      fetchRP();
      fetchInspections();
    }, 100);
    return () => clearTimeout(timer);
  }, [coreData, fetchOwnership, fetchRP, fetchInspections, analysis]);

  // Handle download via presigned S3 URL
  const handleDownload = async () => {
    if (!uuid) return;
    try {
      const { data } = await api.get(`/reports/${uuid}/download/`);
      if (data.url) {
        window.open(data.url, '_blank');
      }
    } catch { /* file may not exist */ }
  };

  // Loading states — only block on analysis fetch, not facility detail
  if (analysisLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !analysis) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/')} sx={{ mb: 2 }}>
          Back to Reports
        </Button>
        <Alert severity="error">{error || 'No data available'}</Alert>
      </Box>
    );
  }

  // Non-admin users can only view completed reports
  if (analysis.status !== 'completed' && !user?.is_superuser) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/')} sx={{ mb: 2, color: '#6B7280', textTransform: 'none', fontWeight: 500, pl: 0 }}>
          Reports
        </Button>
        <Alert severity="info">
          {analysis.report_type === 'mediation'
            ? "Your mediation report is being prepared. You'll be notified when it's ready."
            : "This report is still being processed. You'll be notified when it's ready."}
        </Alert>
      </Box>
    );
  }

  const isAdmin = !!user?.is_superuser;
  const isMediation = analysis.report_type === 'mediation';

  // Admin: attach a file. On a mediation report the first upload delivers it.
  const handleUpload = async (file: File) => {
    setUploadError('');
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post(`/reports/${analysis.uuid}/documents/upload/`, form);
      setAnalysis(a => a && { ...a, documents: data.documents, status: data.status });
    } catch (e: any) {
      setUploadError(e.response?.data?.error || 'Upload failed');
    }
    setUploading(false);
  };

  const handleRemoveDoc = async (docIdx: number, name: string) => {
    if (!window.confirm(`Remove "${name}" from this report?`)) return;
    try {
      const { data } = await api.delete(`/reports/${analysis.uuid}/documents/${docIdx}/remove/`);
      setAnalysis(a => a && { ...a, documents: data.documents, status: data.status });
    } catch (e: any) {
      setUploadError(e.response?.data?.error || 'Remove failed');
    }
  };

  const uploadButton = isAdmin ? (
    <>
      <input
        ref={uploadInputRef}
        type="file"
        hidden
        accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
        data-testid="upload-input"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleUpload(file);
          e.target.value = '';
        }}
      />
      <Button
        variant="outlined"
        size="small"
        startIcon={uploading ? <CircularProgress size={14} /> : <UploadFileIcon />}
        disabled={uploading}
        onClick={() => uploadInputRef.current?.click()}
        sx={{ borderColor: '#102B3D', color: '#102B3D', textTransform: 'none', fontWeight: 600 }}
      >
        {uploading ? 'Uploading...' : 'Upload document'}
      </Button>
    </>
  ) : null;

  const documentsPanel = (
    <>
      {uploadError && <Alert severity="error" sx={{ mb: 1.5 }}>{uploadError}</Alert>}
            {analysis.documents && analysis.documents.length > 0 ? (() => {
              const groups = [
                { type: 'mediation', label: 'Mediation Report' },
                { type: 'case_summary', label: 'Case Summary' },
                { type: 'analysis', label: 'Financial and Staffing Analysis' },
                { type: 'cost_report', label: 'Cost Reports (HCRIS)' },
                { type: 'pbj_analysis', label: 'PBJ Staffing Data' },
                { type: 'pbj_ant_rec', label: 'PBJ Daily Staffing Analysis' },
                { type: 'mds_raw', label: 'MDS Data (Assessments)' },
                { type: 'upload', label: 'Other Documents' },
              ];
              const indexedDocs = analysis.documents.map((doc, idx) => ({ ...doc, _idx: idx }));

              const handleDocDownload = async (docIdx: number, fileName: string) => {
                try {
                  const response = await api.get(
                    `/reports/${analysis.uuid}/documents/${docIdx}/download/`,
                    { responseType: 'blob' }
                  );
                  const contentType = response.headers['content-type'] || '';
                  if (contentType.includes('json')) {
                    const text = await (response.data as Blob).text();
                    const { url } = JSON.parse(text);
                    if (url) window.open(url, '_blank');
                  } else {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = fileName;
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                  }
                } catch { /* file may not exist */ }
              };

              return (
                <Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1.5 }}>
                  {uploadButton}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Download />}
                    onClick={async () => {
                      try {
                        const response = await api.get(
                          `/reports/${analysis.uuid}/documents/download-all/`,
                          { responseType: 'blob' }
                        );
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `${analysis.facility_name || analysis.ccn} - Documents.zip`;
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                      } catch { /* */ }
                    }}
                    sx={{ borderColor: '#10A4C2', color: '#10A4C2', '&:hover': { borderColor: '#0D8DA7', bgcolor: '#E8F8FB' } }}
                  >
                    Download All (.zip)
                  </Button>
                </Box>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableBody>
                      {groups.map(group => {
                        const docs = indexedDocs.filter(d => d.type === group.type);
                        if (docs.length === 0) return null;
                        return (
                          <React.Fragment key={group.type}>
                            <TableRow>
                              <TableCell
                                colSpan={3}
                                sx={{ bgcolor: '#EAF1F5', py: 1, borderBottom: '1px solid #D7E1E8' }}
                              >
                                <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#102B3D' }}>
                                  {group.label}
                                </Typography>
                              </TableCell>
                            </TableRow>
                            {docs.map(doc => (
                              <TableRow key={doc._idx} hover>
                                <TableCell sx={{ width: 36, pr: 0, pl: 2 }}>
                                  {doc.type === 'analysis'
                                    ? <TableChartIcon sx={{ color: '#10A4C2', fontSize: 20 }} />
                                    : <DescriptionIcon sx={{ color: '#6B7280', fontSize: 20 }} />}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">{doc.name}</Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ width: isAdmin ? 150 : 110, whiteSpace: 'nowrap' }}>
                                  <Button
                                    size="small"
                                    startIcon={<Download />}
                                    onClick={() => handleDocDownload(doc._idx, doc.file_key.split('/').pop() || `${doc.name}.xlsx`)}
                                    sx={{ color: '#10A4C2' }}
                                  >
                                    Download
                                  </Button>
                                  {isAdmin && (
                                    <Tooltip title="Remove from this report">
                                      <IconButton size="small" aria-label={`Remove ${doc.name}`} onClick={() => handleRemoveDoc(doc._idx, doc.name)}>
                                        <DeleteOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                </Box>
              );
            })() : (
              <Box>
                {uploadButton && <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>{uploadButton}</Box>}
                <Alert severity="info">
                  {isMediation
                    ? 'The mediation report will be available here once it is ready.'
                    : 'Documents will be available once the report is generated.'}
                </Alert>
              </Box>
            )}
    </>
  );

  const facility = coreData?.facility;
  const reportYears = coreData?.facility_years?.map((fy: any) => fy.year);

  return (
    <Box>
      {/* Breadcrumb */}
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/')}
        sx={{ mb: 2, color: '#6B7280', textTransform: 'none', fontWeight: 500, pl: 0 }}
      >
        Reports
      </Button>

      {/* Report Header */}
      <Paper sx={{ p: 0, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        {/* Top section — facility name + actions */}
        <Box sx={{ px: 3, pt: 2.5, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{
                color: '#102B3D',
                lineHeight: 1.3,
                '&:hover': { outline: '1px solid #D7E1E8', borderRadius: 0.5, px: 0.5, mx: -0.5 },
                '&:focus': { outline: '2px solid #10A4C2', borderRadius: 0.5, px: 0.5, mx: -0.5, bgcolor: '#fff' },
              }}
              contentEditable
              suppressContentEditableWarning
              onBlur={async (e) => {
                const newName = (e.target as HTMLElement).textContent?.trim() || '';
                const currentName = analysis.display_name || analysis.facility_name;
                if (newName && newName !== currentName) {
                  try {
                    await api.patch(`/reports/${analysis.uuid}/rename/`, { display_name: newName });
                    setAnalysis({ ...analysis, display_name: newName });
                  } catch { /* ignore */ }
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLElement).blur(); }
                if (e.key === 'Escape') {
                  (e.target as HTMLElement).textContent = analysis.display_name || analysis.facility_name || '';
                  (e.target as HTMLElement).blur();
                }
              }}
            >
              {analysis.display_name || analysis.facility_name || facility?.facility_name}
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <ReportTypeChip type={analysis.report_type} />
            </Box>
            {formatAddress(facility) && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                {formatAddress(facility)}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            {user?.is_superuser && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setNotifyModalOpen(true)}
                sx={{ borderColor: '#102B3D', color: '#102B3D', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#EAF1F5' } }}
              >
                Notify Customer
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              onClick={() => setContactModalOpen(true)}
              sx={{ borderColor: '#10A4C2', color: '#10A4C2', textTransform: 'none', fontWeight: 600, '&:hover': { borderColor: '#0D8DA7', bgcolor: '#E8F8FB' } }}
            >
              Contact Ernie
            </Button>
          </Box>
        </Box>

        {/* Bottom section — structured details grid */}
        <Box sx={{ px: 3, py: 2, bgcolor: '#EEF2F6', borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {[
            { label: 'CCN', value: analysis.ccn, mono: true },
            { label: 'State', value: facility?.state || analysis.state || '—' },
            { label: 'Beds', value: facility?.beds ?? '—' },
            { label: 'Avg Census', value: facility?.avg_census ? Math.round(facility.avg_census) : '—' },
            { label: 'Report Period', value: analysis.period || '—' },
            { label: 'Fiscal Years', value: reportYears?.length ? [...reportYears].sort().join(', ') : '—' },
          ].map(({ label, value, mono }) => (
            <Box key={label}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.05em' }}>
                {label}
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={mono ? { fontFamily: 'monospace' } : undefined}>
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Mediation report: documents only, no facility tabs */}
      {isMediation ? (
        <Box>{documentsPanel}</Box>
      ) : (
      <>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="Staffing" value={0} />
              <Tab label="Financials" value={1} />
              <Tab label="Prior Conduct" value={2} />
              <Tab label="Ernie's Insights" value={3} />
              <Tab label="Ownership" value={4} />
              <Tab label="Documents" value={5} />
            </Tabs>
          </Box>

          {/* Tab 5: Documents — doesn't need facility data */}
          <TabPanel value={tabValue} index={5}>
            {documentsPanel}

          </TabPanel>

          {coreLoading || !coreData ? (
            /* Tabs 0-4 need facility data; Documents (5) does not */
            tabValue < 5 ? (
              facilityError ? (
                <Alert severity="error" sx={{ mt: 2 }}>{facilityError}</Alert>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress />
                </Box>
              )
            ) : null
          ) : (
          <>
          {/* Tab 0: Understaffing */}
          <TabPanel value={tabValue} index={0}>
            <UnderstaffingSummary
              facility={facility}
              facilityYears={coreData.facility_years}
              stateContext={coreData.state_context}
              turnoverBenchmarks={coreData.turnover_benchmarks}
              costReports={coreData.cost_reports}
              model={acuityModel}
              onModelChange={setAcuityModel}
            />
            <Box sx={{ mt: 3 }}>
              <AcuityGapCharts
                ccn={analysis.ccn}
                model={acuityModel}
                availableYears={analysis.report_years || []}
              />
            </Box>
            <Box sx={{ mt: 3 }}>
              <StaffingTrendCard
                ccn={analysis.ccn}
                startYear={Math.min(...(analysis.report_years || [2020]))}
                endYear={Math.max(...(analysis.report_years || [2025]))}
                model={acuityModel}
              />
            </Box>
          </TabPanel>

          {/* Tab 1: Financials */}
          <TabPanel value={tabValue} index={1}>
            <FinancialsTab
              ccn={analysis.ccn}
              reportIds={analysis.report_ids || []}
            />
          </TabPanel>

          {/* Tab 2: Prior Conduct */}
          <TabPanel value={tabValue} index={2}>
            {inspectionLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <PriorConduct
                ccn={analysis.ccn}
                state={facility?.state || analysis.state || ''}
                beds={facility?.beds}
                startYear={Math.min(...(analysis.report_years || [2020])) - 1}
                endYear={Math.max(...(analysis.report_years || [2025]))}
                facilityYears={coreData.facility_years}
                facilityMetrics={facility}
                citationBenchmarks={coreData.citation_benchmarks}
              />
            )}
          </TabPanel>

          {/* Tab 3: Ernie's Insights */}
          <TabPanel value={tabValue} index={3}>
            <ErnieInsights
              ccn={analysis.ccn}
              years={analysis.report_years || undefined}
            />
          </TabPanel>

          {/* Tab 4: Ownership */}
          <TabPanel value={tabValue} index={4}>
            {ownershipLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : ownershipData ? (
              <OwnershipTab
                ccn={analysis.ccn}
                ownershipData={ownershipData}
                onNavigate={() => {}}
              />
            ) : (
              <Alert severity="info">Loading ownership data...</Alert>
            )}
          </TabPanel>
          </>
          )}
      </>
      )}

      {analysis && (
        <>
          <ContactErnieModal
            open={contactModalOpen}
            onClose={() => setContactModalOpen(false)}
            report={analysis}
          />
          <NotifyCustomerModal
            open={notifyModalOpen}
            onClose={() => setNotifyModalOpen(false)}
            report={analysis}
            customerEmail={analysis.requested_by_email || ''}
          />
        </>
      )}
    </Box>
  );
}
